import React, { useCallback, useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import styles from './Form.module.scss';
import Actions from '../actions/Actions';
import { useNextContent } from '../../useNextContent';
import { FeedbackForm, Lesson, ExtendedUnit, ExtendedHeaUnit } from '@types';
import FormGenerateContent from './FormGenerateContent';
import FormPreview from './FormPreview';
import FormResult from './FormResult';
import FeedbackFormScroll from '@pages/feedback-form/feedback-form/FeedbackFormScroll';
import { isHomeEDApp, SURVEY_TYPE } from '@constants';
import { useHeaCreateFeedbackFormAnswerMutation } from '@api/graphql-codegen';
import { useCourseContent } from '@src/hooks';
import { getMediaAbsolutePath } from '@services/File';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import Spinner from '@components/spinner/Spinner';
import Button from '@components/button/Button';
import { isFilePath } from '@src/utils/helpers';
// Use singleton worker to prevent destruction by React StrictMode
import pdfWorkerSingleton from '../../../../utils/pdfWorkerSingleton';
import { logger } from '../../../../utils/logger';

interface SlideMessage {
  type: string;
  totalSlides?: number;
  currentPage?: number;
  page?: number;
  subPage?: number;
}

type Props = {
  data?: string | '';
  pageNumber?: number | 0;
  formData?: string | null;
  isFullScreen: boolean;
  id: string;
  name: string;
  isPreview?: boolean;
  goBack?: () => void;
  studyPlanId?: string;
  lesson?: Lesson;
  unit?: ExtendedUnit | ExtendedHeaUnit;
  changePageNumber: (value: number) => void
  startLesson?: () => void;
};

function Form(props: Props) {
  const { data, pageNumber, formData, isFullScreen, name, goBack, isPreview, id: moduleId, studyPlanId, lesson, unit, changePageNumber, startLesson } = props;


  const { hasNextContent, selectNext, selectPrev } = useNextContent();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const { totalPages, setTotalPages, showExitTicket, setShowExitTicket, isViewExitTicket } = useCourseContent();
  const [createHeSurveyAnswer, { loading }] = useHeaCreateFeedbackFormAnswerMutation();

  const [isComplete, setIsComplete] = useState<boolean>(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // show slide data
  useEffect(() => {
    setTotalPages(0);
    if (data) {
      setShowExitTicket(false);
    } else {
      setShowExitTicket(true);
    }
  }, [data, setShowExitTicket, setTotalPages]);

  useEffect(() => {
    // Function to handle messages from iframe
    const handleMessage = (event: MessageEvent<SlideMessage>) => {
      if (event.data.type === "SLIDE_INFO") {
        changePageNumber(event.data.subPage || 1);
        setTotalPages(event.data.totalSlides || 1);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [setTotalPages, changePageNumber]);

  const fileObj = useMemo(() => {
    if (data && isFilePath(data)) {
      return getMediaAbsolutePath(data);
    }
    return null;
  }, [data]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setTotalPages(numPages);
  }, [setTotalPages]);

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef.current.getBoundingClientRect();
      setWidth(width);
      setHeight(height);
    }
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('resize', calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener('resize', calculateSize);
    };
  }, [calculateSize]);

  useEffect(() => {
    setTimeout(calculateSize, 50);
  }, [calculateSize]);

  const prevDisabled = useMemo(() => {
    return pageNumber ? pageNumber <= 1 : false
  }, [pageNumber]);

  const isLast = useMemo(() => {
    return pageNumber ? pageNumber === totalPages : false
  }, [pageNumber, totalPages]);

  const handleLeft = useCallback(() => {
    if (pageNumber) {
      changePageNumber(Math.max(pageNumber - 1, 1));
      if (iframeRef.current) {
        iframeRef.current.contentWindow?.postMessage({ type: "PREV_SLIDE" }, "*");
      }
      if (!prevDisabled) setShowExitTicket(false);
    } else {
      selectPrev();
    }
  }, [pageNumber, prevDisabled, setShowExitTicket, changePageNumber, selectPrev]);

  const handleRight = useCallback(() => {
    if (pageNumber) {
      changePageNumber(Math.min(pageNumber + 1, totalPages));
      if (iframeRef.current) {
        iframeRef.current.contentWindow?.postMessage({ type: "NEXT_SLIDE" }, "*");
      }
    }
    if (isLast) {
      setShowExitTicket(true);
    }
  }, [totalPages, pageNumber, changePageNumber, isLast, setShowExitTicket]);

  const prevButton = useMemo(() => {
    if (prevDisabled) return null;
    if (!data) return null;
    return <Button size={'extraSmall'} onClick={handleLeft} label={'Previous'} />;
  }, [data, prevDisabled, handleLeft]);

  const nextButton = useMemo(() => {
    if (showExitTicket) return null;
    if (!data) return null;
    return <Button size={'extraSmall'} onClick={handleRight} label={'Next'} />;
  }, [data, showExitTicket, handleRight]);
  //

  const jsonFormData = useMemo(() => {
    if (!formData) return null;
    try {
      return JSON.parse(formData) as FeedbackForm;
    } catch (e) {
      logger.info('parse error-', e, formData);
      return null
    }
  }, [formData]);

  const exitTicket = useMemo(() => (jsonFormData ? true : false), [jsonFormData]);
  const showMarkCompleteModal = useMemo(() => {
    return (exitTicket && !isComplete && !hasNextContent) ? true : false
  }, [exitTicket, hasNextContent, isComplete])

  const setComplete = useCallback(() => {
    setIsComplete(true);
  }, []);

  const onHomeEdFormSubmitAnswer = useCallback(async (formDataWithAnswers: string) => {
    if (studyPlanId) {
      await createHeSurveyAnswer({
        variables: {
          input: {
            moduleId,
            answerData: formDataWithAnswers,
            studyplanId: studyPlanId,
            surveyType: exitTicket ? SURVEY_TYPE.EXIT_TICKET : SURVEY_TYPE.RESEARCH,
            moduleName: name,
            unitId: unit?.id,
            unitName: unit?.name,
            lessonId: lesson?.id,
            lessonName: `${lesson?.number !== undefined && lesson?.number !== null ? `#${lesson?.number} ` : ''}${lesson?.name}`,
          }
        }
      });
      if (showMarkCompleteModal) {
        setComplete();
      } else {
        selectNext();
      }
    } else {
      if (showMarkCompleteModal) {
        setComplete();
      } else {
        selectNext();
      }
    }
  }, [createHeSurveyAnswer, exitTicket, lesson?.id, lesson?.name, lesson?.number, moduleId, name, selectNext, setComplete, showMarkCompleteModal, studyPlanId, unit?.id, unit?.name]);

  const ratio = useMemo(() => {
    if (width && height) {
      return width / height
    }
    return 1
  }, [width, height])

  const iframeSrc = useMemo(() => {
    return isPreview ? `${props.data}/index-clear.html` : `${props.data}/index-black.html`;
  }, [isPreview, props.data])

  if (!jsonFormData) return null;

  return (
    <Actions
      fullScreen={isFullScreen}
      leftButton={prevButton}
      rightButton={nextButton}
      totalPages={totalPages}
      pageNumber={pageNumber}
      className={isViewExitTicket ? styles.blackActionsWrapper : styles.actionsWrapper}
      hideNextButton={isHomeEDApp || (!isComplete && showMarkCompleteModal && !hasNextContent)}
      goBack={goBack} exitTicket={exitTicket}
      isPreview={isPreview}
      formData={formData}
    >
      {data && !showExitTicket ?
        <div className={`${styles.slideWrapper} ${!isPreview ? styles.fullScreen : ''}`} ref={wrapperRef}>
          <div className={isFullScreen ? styles.fullContent : styles.content}>
            {fileObj ?
              <Document
                file={fileObj}
                onLoadSuccess={onDocumentLoadSuccess}
                className={styles.document}
                loading={<Spinner />}
              >
                <Page loading={<Spinner />} pageNumber={pageNumber} width={ratio > 1.7 ? 0 : width}
                  height={height} />
              </Document> :
              (<div
                style={{
                  width: `${width}px`,
                  height: `${height}px`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <iframe
                  ref={iframeRef}
                  src={iframeSrc}
                  title="Zip Slide"
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              </div>)
            }
            {isPreview && !isHomeEDApp ?
              <div className={styles.previewBeginLesson}>
                <div className={styles.pulse}>
                  <Button label={'Begin Lesson'} color='white' className={styles.beginLesson} onClick={() => startLesson && startLesson()} />
                </div>
              </div> :
              null
            }
          </div>
        </div> :
        <>
          {isViewExitTicket ?
            <FormResult jsonFormData={jsonFormData} moduleId={moduleId} studyPlanId={studyPlanId} /> :
            (isPreview ?
              <FormPreview jsonFormData={jsonFormData} /> :
              (isHomeEDApp ?
                <div className={styles.scrollView}>
                  <FeedbackFormScroll onSubmit={onHomeEdFormSubmitAnswer} form={jsonFormData} loading={loading} />
                </div> :
                <FormGenerateContent
                  jsonFormData={jsonFormData}
                  name={name}
                  goBack={goBack}
                  formData={formData}
                  setComplete={setComplete}
                  showMarkCompleteModal={showMarkCompleteModal}
                />
              )
            )
          }
        </>}
    </Actions>
  );
}

export default Form;
